import React, { useState } from 'react';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { Briefcase, CalendarPlus, Circle, FileText, Plus, Receipt } from 'lucide-react';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import {
	DropdownMenu,
	DropdownMenuTrigger,
	DropdownMenuContent,
	DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/ui/custom/StatusBadge';
import { Skeleton } from '@/components/ui/skeleton';
import useJobs from '@/hooks/runner/useJobs';
import useHasRecentQuoteOrInvoice from '@/hooks/runner/useHasRecentQuoteOrInvoice';
import { getAddEventLink } from '@/hooks/runner/calendar/useAddEventLink';
import useInvoices from '@/hooks/runner/useInvoices';
import useQuotes from '@/hooks/runner/useQuotes';
import useOpportunities from '@/hooks/runner/useOpportunities';
import usePersistentOpen from '@/hooks/usePersistentOpen';
import { useIsMobile } from '@/hooks/use-mobile';
import { ActionSheet } from '@/components/mobile';
import { DetailsSectionActions, DetailsSectionHeader } from './DetailsSection';

interface WorkItem {
	_project: string;
	type: string;
	name: string;
	number: number;
	status?: string;
	category?: string;
	total?: number;
	estimatedValue?: number;
	updatedAt?: string;
	startDate?: string;
}

const TYPE_CONFIG: Record<string, { color: string; label: string }> = {
	quote: { color: '#A358DF', label: 'Quote' },
	job: { color: '#0090DA', label: 'Job' },
	invoice: { color: '#FFCB00', label: 'Invoice' },
	opportunity: { color: '#FF0476', label: 'Opportunity' },
};

const CustomerWork = ({ _customer }: { _customer: string }) => {
	const navigate = useNavigate();
	const isMobile = useIsMobile();
	const [createSheetOpen, setCreateSheetOpen] = useState(false);
	const [isOpen, setIsOpen] = usePersistentOpen(
		'conversation-details:customer-work',
	);

	const skip = !isOpen;
	const { jobs = [], isLoading: jobsLoading } = useJobs({ _customer, skip });
	const { invoices = [], isLoading: invoicesLoading } = useInvoices({
		_customer,
		skip,
	});
	const { quotes = [], isLoading: quotesLoading } = useQuotes({
		_customer,
		skip,
	});
	const { opportunities = [], isLoading: oppsLoading } = useOpportunities({
		_customer,
		skip,
	});

	const loading = jobsLoading || invoicesLoading || quotesLoading || oppsLoading;

	const items: WorkItem[] = [
		...jobs,
		...quotes,
		...invoices,
		...opportunities,
	].sort(
		(a, b) =>
			new Date(b.updatedAt ?? 0).getTime() -
			new Date(a.updatedAt ?? 0).getTime(),
	);

	// Without recent quotes or invoices, jobs are calendar events, as on the calendar
	const usesWorksBilling = useHasRecentQuoteOrInvoice();
	const isEvent = (item: WorkItem) => item.type === 'job' && !usesWorksBilling;

	const handleClick = (item: WorkItem) => {
		if (isEvent(item)) {
			navigate(
				item.startDate
					? `/work/calendar?date=${moment(item.startDate).format('YYYY-MM-DD')}`
					: '/work/calendar'
			);
			return;
		}
		navigate(`/work/view/${item.type}/${item._project}`);
	};

	const handleCreate = (workflow: string) => {
		navigate(`/work/create/${workflow}?_customer=${_customer}`);
	};
	const createLabel = `Create quote, ${usesWorksBilling ? 'job' : 'event'} or invoice`;

	return (
		<div className="border-b border-border/60">
			<Collapsible open={isOpen} onOpenChange={setIsOpen}>
				<DetailsSectionHeader
					label="Work"
					title={<>Work ({items.length})</>}
					actions={
						<DetailsSectionActions>
							{isMobile ? (
								<Button
									variant="ghost"
									size="icon"
									className="size-11 rounded-full text-muted-foreground"
									aria-label={createLabel}
									aria-haspopup="dialog"
									onClick={() => setCreateSheetOpen(true)}
								>
									<Plus className="size-5" />
								</Button>
							) : (
								<DropdownMenu>
									<DropdownMenuTrigger asChild>
										<Button
											variant="ghost"
											size="icon"
											className="h-6 w-6 text-muted-foreground"
											aria-label={createLabel}
										>
											<Plus className="h-4 w-4" />
										</Button>
									</DropdownMenuTrigger>
									<DropdownMenuContent align="end">
										<DropdownMenuItem onClick={() => handleCreate('quote')}>
											Quote
										</DropdownMenuItem>
										{usesWorksBilling ? (
											<DropdownMenuItem onClick={() => handleCreate('job')}>
												Job
											</DropdownMenuItem>
										) : (
											<DropdownMenuItem
												onClick={() => navigate(getAddEventLink({ _customer }))}
											>
												Event
											</DropdownMenuItem>
										)}
										<DropdownMenuItem onClick={() => handleCreate('invoice')}>
											Invoice
										</DropdownMenuItem>
									</DropdownMenuContent>
								</DropdownMenu>
							)}
						</DetailsSectionActions>
					}
				/>
				<CollapsibleContent>
					<div className="px-4 pb-3">
						{loading ? (
							<div className="space-y-2">
								<Skeleton className="h-12 w-full" />
								<Skeleton className="h-12 w-full" />
							</div>
						) : items.length === 0 ? (
							<div className="text-center py-6 text-muted-foreground">
								<p className="text-sm">No work items yet</p>
							</div>
						) : (
							<div className="space-y-2">
								{items.map((item) => {
									const event = isEvent(item);
									const cfg = event
										? { ...TYPE_CONFIG.job, label: 'Event' }
										: (TYPE_CONFIG[item.type] ??
											TYPE_CONFIG.opportunity);
									const displayStatus =
										item.type === 'opportunity'
											? item.category
											: item.status;

									return (
										<div
											key={`${item.type}-${item._project}`}
											role="link"
											tabIndex={0}
											onClick={() => handleClick(item)}
											onKeyDown={(keyEvent) => {
												if (keyEvent.key === 'Enter') handleClick(item);
											}}
											className="p-3 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring max-md:active:bg-muted/60"
										>
											<div className="flex items-center justify-between mb-1.5">
												<div className="flex items-center gap-1.5 min-w-0">
													<Circle
														fill={cfg.color}
														color={cfg.color}
														className="shrink-0"
														size={10}
													/>
													<span className="text-xs text-muted-foreground">
														{cfg.label}
													</span>
													{!event && (
														<span className="text-xs text-muted-foreground">
															#{item.number}
														</span>
													)}
												</div>
												{event ? (
													item.startDate && (
														<span className="text-xs text-muted-foreground shrink-0">
															{moment(item.startDate).format('lll')}
														</span>
													)
												) : (
													displayStatus && (
														<StatusBadge
															status={displayStatus}
														/>
													)
												)}
											</div>
											<h4 className="font-medium text-sm line-clamp-1 max-md:text-base">
												{item.name}
											</h4>
										</div>
									);
								})}
							</div>
						)}
					</div>
				</CollapsibleContent>
			</Collapsible>
			{isMobile && (
				<ActionSheet
					open={createSheetOpen}
					onOpenChange={setCreateSheetOpen}
					title="Create for this customer"
					items={[
						{ label: 'Quote', icon: FileText, onSelect: () => handleCreate('quote') },
						usesWorksBilling
							? { label: 'Job', icon: Briefcase, onSelect: () => handleCreate('job') }
							: {
									label: 'Event',
									icon: CalendarPlus,
									onSelect: () => navigate(getAddEventLink({ _customer })),
								},
						{ label: 'Invoice', icon: Receipt, onSelect: () => handleCreate('invoice') },
					]}
				/>
			)}
		</div>
	);
};

export default CustomerWork;
